import {
  Box,
  FormHelperText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { DeepcellTextField } from 'components/shared'
import { Run, RunSamplingConfig } from 'utils/api'
import { useState, useEffect, useMemo, useCallback } from 'react'

interface CustomSamplingConfigProps {
  runs: Run[]
  maxCellCount: number
  samplingConfig: RunSamplingConfig[]
  onConfigChange: (config: RunSamplingConfig[]) => void
  /**
   * Cell records actually uploaded per run, keyed by run_id. Supplied by the parent so this
   * component stays presentational. A value may be null when the backend has no count available;
   * omitted entirely means we simply have not been told, and both fall back to total_cell_count.
   */
  cellRecordCounts?: Record<string, number | null>
}

export const CustomSamplingConfig = ({
  runs,
  maxCellCount,
  samplingConfig,
  onConfigChange,
  cellRecordCounts,
}: CustomSamplingConfigProps): JSX.Element => {
  const theme = useTheme()
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [warnings, setWarnings] = useState<Record<string, string>>({})

  /**
   * Cells available to browse for a run: the uploaded record count when we have one, otherwise
   * the Controller total. The null check must be explicit -- a genuine count of 0 is a real
   * answer, and `||` would silently replace it with total_cell_count.
   */
  const availableCellsFor = useCallback(
    (run: Pick<Run, 'run_id' | 'total_cell_count'> | undefined): number => {
      if (!run) return 0
      const recorded = cellRecordCounts?.[run.run_id]
      if (recorded !== undefined && recorded !== null) return recorded
      return run.total_cell_count || 0
    },
    [cellRecordCounts]
  )

  // Derive initialization state from data - we're initializing when config is empty but runs exist
  // Once onConfigChange populates the config, this automatically becomes false on next render
  const isInitializing = samplingConfig.length === 0 && runs.length > 0

  /**
   * Convert percentage to target cell count, respecting rounding
   * targetCount = Math.round(maxCellCount * percentage / 100)
   */
  const percentageToCellCount = useCallback((percentage: number): number => {
    return Math.round((maxCellCount * percentage) / 100)
  }, [maxCellCount])

  /**
   * Calculate actual cell counts by clamping to available cells.
   * User percentages are preserved as-is (treated as intent).
   * No redistribution - backend handles final clamping.
   */
  const calculateCellCounts = useCallback((configs: RunSamplingConfig[]): {
    totalCells: number
    cellCountsByRun: Map<string, number>
  } => {
    const cellCountsByRun = new Map<string, number>()
    let totalCells = 0

    configs.forEach((config) => {
      const run = runs.find((r) => r.run_id === config.run_id)
      const available = availableCellsFor(run)
      const percentage = config.percentage || 0
      const targetCount = percentageToCellCount(percentage)

      // Clamp to available cells for display purposes
      const actualCount = Math.min(targetCount, available)
      cellCountsByRun.set(config.run_id, actualCount)
      totalCells += actualCount
    })

    return { totalCells, cellCountsByRun }
  }, [runs, percentageToCellCount, availableCellsFor])

  // Initialize config if empty - start with equal integer percentages summing to 100
  useEffect(() => {
    if (samplingConfig.length === 0 && runs.length > 0) {
      // Start with equal percentages (e.g., 50/50 for 2 runs, 33/33/34 for 3 runs)
      const equalPercentage = Math.floor(100 / runs.length)
      const remainder = 100 - equalPercentage * runs.length

      const configs: RunSamplingConfig[] = runs.map((run, index) => {
        // Distribute remainder to last runs to get exactly 100%
        const percentage = index >= runs.length - remainder ? equalPercentage + 1 : equalPercentage

        return {
          run_id: run.run_id,
          percentage,
        }
      })

      onConfigChange(configs)
    }
  }, [runs, samplingConfig.length, onConfigChange])

  // Calculate totals for validation and display
  const totals = useMemo(() => {
    const { totalCells } = calculateCellCounts(samplingConfig)
    // Use RAW user input for totalPercentage (not processed) to show actual sum
    const totalPercentage = samplingConfig.reduce((sum, config) => sum + (config.percentage || 0), 0)

    return { totalCount: totalCells, totalPercentage }
  }, [samplingConfig, calculateCellCounts])

  // Validate inputs and show appropriate warnings/errors
  useEffect(() => {
    if (isInitializing) {
      setErrors({})
      setWarnings({})
      return
    }

    const newErrors: Record<string, string> = {}
    const newWarnings: Record<string, string> = {}

    // Validate individual percentages
    samplingConfig.forEach((config, index) => {
      const key = `${config.run_id}-${index}`

      const percentage = config.percentage || 0
      if (percentage < 0 || percentage > 100) {
        newErrors[key] = 'Must be between 0 and 100'
      }
    })

    // Validate total percentage first
    const { totalPercentage, totalCount } = totals

    if (totalPercentage > 100) {
      // Total exceeds 100% - show blocking error
      newErrors.total = `Total percentage is ${totalPercentage}%. Must not exceed 100%.`
    } else {
      // Validate total based on final cell count (clamped to available)
      const percentOfMax = maxCellCount > 0 ? (totalCount / maxCellCount) * 100 : 0

      if (totalCount === maxCellCount) {
        // Perfect - no warnings or errors
      } else if (totalCount >= maxCellCount * 0.99) {
        // Within 99% - allow silently (rounding tolerance)
      } else if (totalCount >= maxCellCount * 0.95) {
        // Between 95% and 99% - show warning
        newWarnings.total = `Total is ${totalCount.toLocaleString()} cells (${percentOfMax.toFixed(1)}% of max). Consider adjusting percentages to reach closer to ${maxCellCount.toLocaleString()} cells.`
      } else {
        // Less than 95% - show blocking error
        newErrors.total = `Total is only ${totalCount.toLocaleString()} cells (${percentOfMax.toFixed(1)}% of max). Must be at least 95% of ${maxCellCount.toLocaleString()} cells.`
      }
    }

    setErrors(newErrors)
    setWarnings(newWarnings)
  }, [isInitializing, samplingConfig, maxCellCount, totals])

  const handlePercentageChange = (runId: string, value: string) => {
    // Allow empty string for better UX
    if (value === '') {
      const updated = samplingConfig.map((config) =>
        config.run_id === runId
          ? { ...config, percentage: undefined }
          : config
      )
      onConfigChange(updated)
      return
    }

    const numValue = parseFloat(value)
    if (Number.isNaN(numValue) || numValue < 0) return

    // Round to integer and clamp to 0-100
    const roundedValue = Math.min(100, Math.max(0, Math.round(numValue)))

    // Simply update the percentage - user's input is their intent
    // Don't auto-redistribute or normalize - let user control all values
    const updated = samplingConfig.map((config) =>
      config.run_id === runId
        ? { ...config, percentage: roundedValue }
        : config
    )

    onConfigChange(updated)
  }

  const getRunDisplayName = (run: Run): string => {
    return run.description || run.run_id || 'Unknown Run'
  }

  return (
    <Stack spacing={2} sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
        Custom Sampling Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
        Specify what percentage of the UMAP (max {maxCellCount.toLocaleString()} cells) should come from each run.
      </Typography>

      <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '8px' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Run</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Available Cells
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                % of UMAP
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Cell Count
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run, index) => {
              const config = samplingConfig.find((c) => c.run_id === run.run_id)
              const availableCells = availableCellsFor(run)
              const key = `${run.run_id}-${index}`
              const error = errors[key]

              const displayValue = config?.percentage ?? ''
              const percentForCalc = typeof displayValue === 'number' ? displayValue : 0
              // Calculate cell count from percentage of maxCellCount
              const calculatedCount = Math.min(
                percentageToCellCount(percentForCalc),
                availableCells
              )

              return (
                <TableRow key={run.run_id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getRunDisplayName(run)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{availableCells.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
                      <DeepcellTextField
                        type="number"
                        value={displayValue}
                        onChange={(e) => handlePercentageChange(run.run_id, e.target.value)}
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 1,
                        }}
                        size="small"
                        error={!!error}
                        sx={{ width: '100px' }}
                        onFocus={(e) => e.target.select()}
                        placeholder=""
                      />
                      <Typography variant="body2">%</Typography>
                    </Box>
                    {error && (
                      <FormHelperText error sx={{ m: 0, mt: 0.5 }}>
                        {error}
                      </FormHelperText>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {calculatedCount.toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Total:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {totals.totalPercentage}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totals.totalCount.toLocaleString()} cells
            </Typography>
          </Box>
        </Box>
        {errors.total && (
          <FormHelperText error sx={{ mt: 1 }}>
            {errors.total}
          </FormHelperText>
        )}
        {warnings.total && !errors.total && (
          <FormHelperText sx={{ mt: 1, color: theme.palette.warning.main }}>
            {warnings.total}
          </FormHelperText>
        )}
      </Box>
    </Stack>
  )
}

export default CustomSamplingConfig
